import React from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents
} from 'react-leaflet';
import CurrentLocationControl from './components/CurrentLocationControl';
import SearchControl from './components/SearchControl';
import LocationInfoOverlay from './components/LocationInfoOverlay';
import MapComponent from './components/MapLayerComponent';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import PropTypes from 'prop-types';

const defaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

const RecenterMap = ({ userLocation }) => {
  const map = useMap();
  React.useEffect(() => {
    if (userLocation && userLocation.lat && userLocation.lng) {
      map.setView([userLocation.lat, userLocation.lng], 16);
    }
  }, [userLocation, map]);
  return null;
};

RecenterMap.propTypes = {
  userLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  })
};

const MapView = ({
  mode,
  userLocation,
  selectedLocation,
  setSelectedLocation,
  setAddressDetails,
  handleSearchSelect,
  isMapEditable,
  uploadedGeoJSONLayer,
  featureGroupRef,
  hasLayer,
  setHasLayer,
  setPolygonData,
  setAreaHectares,
  setAreaAcres,
  setPerimeter,
  areaAcres,
  areaHectares,
  perimeter
}) => {
  return (
    <MapContainer
      center={[userLocation.lat, userLocation.lng]}
      zoom={16}
      style={{ height: '70vh', width: '100%' }}
      zoomControl={false}
    >
      <RecenterMap userLocation={userLocation} />
      <TileLayer
        attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>'
        url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_PUBLIC_KEY}`}
      />

      {selectedLocation && (
        <Marker
          position={[selectedLocation.lat, selectedLocation.lng]}
          icon={defaultIcon}
        />
      )}

      {mode === 'location' && (
        <LocationInfoOverlay
          mode="location"
          location={selectedLocation}
          address={selectedLocation?.address}
        />
      )}

      {mode === 'polygon' && (
        <MapComponent
          uploadedGeoJSONLayer={uploadedGeoJSONLayer}
          isMapEditable={isMapEditable}
          featureGroupRef={featureGroupRef}
          hasLayer={hasLayer}
          setHasLayer={setHasLayer}
          setPolygonData={setPolygonData}
          setAreaHectares={setAreaHectares}
          setAreaAcres={setAreaAcres}
          setPerimeter={setPerimeter}
        />
      )}

      {mode === 'polygon' && (
        <LocationInfoOverlay
          mode="polygon"
          location={selectedLocation}
          areaHectares={areaHectares}
          areaAcres={areaAcres}
          perimeter={perimeter}
        />
      )}

      {isMapEditable && (
        <>
          <CurrentLocationControl onLocationFound={handleSearchSelect} />
          <SearchControl onLocationSelect={handleSearchSelect} />
        </>
      )}
    </MapContainer>
  );
};

MapView.defaultProps = {
  mode: 'location',
  userLocation: { lat: 0, lng: 0 },
  selectedLocation: null,
  setSelectedLocation: () => {},
  setAddressDetails: () => {},
  handleSearchSelect: () => {},
  isMapEditable: true,
  uploadedGeoJSONLayer: null,
  featureGroupRef: React.createRef(),
  hasLayer: false,
  setHasLayer: () => {},
  setPolygonData: () => {},
  setAreaHectares: () => {},
  setAreaAcres: () => {},
  setPerimeter: () => {},
  areaAcres: 0,
  areaHectares: 0,
  perimeter: 0
};
MapView.propTypes = {
  mode: PropTypes.oneOf(['location', 'polygon']),
  userLocation: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired
  }),
  selectedLocation: PropTypes.object,
  setSelectedLocation: PropTypes.func.isRequired,
  setAddressDetails: PropTypes.func.isRequired,
  handleSearchSelect: PropTypes.func.isRequired,
  isMapEditable: PropTypes.bool,
  uploadedGeoJSONLayer: PropTypes.object,
  featureGroupRef: PropTypes.object,
  hasLayer: PropTypes.bool,
  setHasLayer: PropTypes.func.isRequired,
  setPolygonData: PropTypes.func.isRequired,
  setAreaHectares: PropTypes.func.isRequired,
  setAreaAcres: PropTypes.func.isRequired,
  setPerimeter: PropTypes.func.isRequired,
  areaAcres: PropTypes.number,
  areaHectares: PropTypes.number,
  perimeter: PropTypes.number
};

export default MapView;
